import {
  Button,
  Drawer as DrawerPopup,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  IconButton,
} from '@fountain/fountain-ui-components';
import { Box, CircularProgress, Collapse, Typography } from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FormattedDate,
  FormattedMessage,
  MessageDescriptor,
  useIntl,
} from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { ADMIN_ROLE } from 'containers/Auth_old/constants';
import { makeSelectWhoami } from 'containers/Auth_old/selectors';
import { addMessageAction } from 'containers/FlashMessage/actions';
import DownloadSvg from 'images/_shared/download.svg';
import CloseIcon from 'images/close.svg';
import globalMessages from 'shared/global/messages';
import { trackAnalyticsEvent } from 'utils/analytics';

import { ApproverRail } from './ApproverRail';
import { Decision, DecisionFooter, ViewerState } from './DecisionFooter';
import {
  EditedDraftFooter,
  OfferEditFooter,
  OfferEditForm,
} from './OfferEditForm';
import messages from './messages';
import { useStyles } from './styles';
import {
  ApprovalRequest,
  PreviousSubmission,
  RequestStatus,
  ReviewEntry,
  RuleGroup,
} from './types';
import {
  isSettled,
  useApprovalRequest,
  useRecordDecision,
} from './useApprovalRequests';
import { useOfferEdit } from './useOfferEdit';

interface OfferReviewPanelProps {
  /** Request external_id. The panel is mounted only when a row is open. */
  requestId: string;
  /**
   * Which control on the queue row opened this. Reported once per open, so the
   * three affordances Design Requirements §5 adds can be told apart from the
   * Review button that was already there.
   */
  entry: ReviewEntry;
  onClose: () => void;
  /** Called after a decision lands, so the queue drops the row. */
  onDecided: () => void;
}

const SUBMISSION_OUTCOMES: Record<
  PreviousSubmission['status'],
  MessageDescriptor
> = {
  approved: messages.submissionApproved,
  rejected: messages.submissionRejected,
  cancelled: messages.submissionCancelled,
};

/**
 * Earlier attempts at this offer (Design Requirements §6, "Decision — submission
 * history").
 *
 * A resubmission is a fresh request with fresh steps, so every level reads
 * `pending` again and the panel otherwise looks identical to a first submission.
 * An approver being asked a second time needs to know that, and the reason a
 * colleague sent it back is the single most useful thing on the surface.
 *
 * Collapsed behind a count, and absent entirely on a first submission: the
 * current offer is what is being decided, and a history section that is always
 * open pushes the approvers it belongs beside off the rail.
 */
const PreviousSubmissions: React.FC<{ submissions: PreviousSubmission[] }> = ({
  submissions,
}) => {
  const styles = useStyles();
  const [open, setOpen] = useState(false);

  if (submissions.length === 0) return null;

  return (
    <div className={styles.railSection}>
      {/* A real button, not a styled div: it toggles content, so it needs to be
          reachable and operable from the keyboard, and `aria-expanded` is what
          says which way it currently is. */}
      <button
        aria-expanded={open}
        className={styles.historyToggle}
        onClick={() => setOpen(current => !current)}
        type="button"
      >
        {open ? (
          <ExpandLessIcon aria-hidden="true" fontSize="small" />
        ) : (
          <ExpandMoreIcon aria-hidden="true" fontSize="small" />
        )}
        <FormattedMessage
          {...messages.previousSubmissions}
          values={{ count: submissions.length }}
        />
      </button>

      {/* `unmountOnExit` because MUI v4's Collapse otherwise only animates the
          height to zero: the rows stay in the DOM, keyboard-reachable and read
          out by a screen reader, while a sighted reader sees a collapsed
          section. */}
      <Collapse in={open} unmountOnExit>
        {submissions.map(submission => (
          <div className={styles.submission} key={submission.number}>
            <Typography variant="body2">
              <FormattedMessage
                {...messages.submissionNumber}
                values={{ number: submission.number }}
              />
              {' · '}
              <FormattedMessage {...SUBMISSION_OUTCOMES[submission.status]} />
              {submission.decidedAt && (
                <>
                  {' · '}
                  <FormattedDate
                    day="numeric"
                    month="short"
                    value={submission.decidedAt}
                    year="numeric"
                  />
                </>
              )}
            </Typography>
            {/* Why it came back. Null unless it was rejected with a reason — an
                approval may carry an optional note, and showing that here would
                read as the reason it was refused. */}
            {submission.rejectionReason && (
              <Typography className={styles.submissionReason} variant="body2">
                {submission.rejectionReason}
              </Typography>
            )}
          </div>
        ))}
      </Collapse>
    </div>
  );
};

/**
 * Why the viewer has nothing to act on — read only when they have nothing.
 *
 * Ordered so an OUTSTANDING obligation wins over a past one. A viewer named on
 * two concurrently-advancing rules can have approved the first and still be the
 * approver on the second's level 2, which has not been reached: "you have already
 * responded" is false there, and `waiting` is the case that sentence exists for.
 *
 * `observer` is the person who submitted the offer. They are a participant — the
 * panel is readable to them — but they hold no assignment on it, so neither of
 * the other two sentences is true.
 */
const viewerStateFor = (request: ApprovalRequest): ViewerState => {
  const ownRows = request.rules.flatMap(rule =>
    rule.levels.flatMap(level =>
      level.approvers.filter(approver => approver.isViewer),
    ),
  );

  // Before anything about the viewer: a finished request is not waiting on a
  // level of sign-off, and their own row can still read `pending` on one that was
  // never asked. Saying "waiting on another level" under a header reading "Offer
  // rejected" is the contradiction this ordering exists to prevent.
  if (isSettled(request)) return 'settled';
  if (ownRows.length === 0) return 'observer';
  if (ownRows.some(approver => approver.status === 'pending')) return 'waiting';
  return 'responded';
};

const TITLES: Record<RequestStatus, MessageDescriptor> = {
  pending: messages.panelTitlePending,
  approved: messages.panelTitleApproved,
  rejected: messages.panelTitleRejected,
  cancelled: messages.panelTitleCancelled,
};

/**
 * The permission behind Edit offer / Revise & resubmit (AXHE-4593).
 *
 * Read off `whoami.perms`, which carries the granted keys verbatim — an admin's
 * set holds `admin` alone, so the role check beside it is not redundant. The
 * server enforces this regardless: every write below goes through
 * `InternalApi::OfferLettersController`, which authorizes the applicant.
 */
const EDIT_OFFERS_PERMISSION = 'edit_offers_pending_approval';

/**
 * The offer statuses that can no longer be edited (`OfferLetter::TERMINAL_STATUSES`).
 *
 * Once a document has gone to the applicant its stored values are the record of
 * what they received, and `OfferLetters::UpdatePreview` refuses to rewrite them.
 * Stated as the closed set rather than its complement so an offer status this
 * build does not know reads as editable and gets the server's own answer, rather
 * than being silently locked out of a control it should have.
 *
 * A null status is the exception and IS locked out: it means the payload carried
 * no offer summary at all, so there is nothing to edit.
 */
const UNEDITABLE_OFFER_STATUSES = ['sent', 'completed', 'canceled'];

/**
 * Which surface the panel is showing.
 *
 * `preview` is not a request status — the request behind it reads whatever it
 * reads — it is the panel holding an edited draft that has been saved but not
 * yet sent back through the rules (Design Requirements §6).
 */
type PanelMode = 'review' | 'edit' | 'preview';

// One frozen instance, so the fallback does not change identity per render and
// re-run every memo downstream of it.
const EMPTY_VALUES: Record<string, string> = {};

/**
 * The level tree as it will be asked again, for the edited-draft preview.
 *
 * Every pill reads Pending because a resubmission raises a fresh request with
 * fresh steps — no approval collected against the previous content carries over,
 * which is exactly what the edit form's banner said would happen. Showing the
 * decisions already recorded would contradict it.
 *
 * An approximation, and honestly so: the rules are matched again at submission,
 * so the request that actually results can have a different shape. This is the
 * closest thing the client holds, and it answers the question the reader has —
 * who is going to be asked.
 */
const asPending = (rules: RuleGroup[]): RuleGroup[] =>
  rules.map(rule => ({
    ...rule,
    levels: rule.levels.map(level => ({
      ...level,
      status: 'pending' as const,
      decidedAt: null,
      escalated: false,
      needsAttention: false,
      approvers: level.approvers.map(approver => ({
        ...approver,
        status: 'pending' as const,
        decidedAt: null,
        comment: null,
      })),
    })),
  }));

/**
 * The in-app offer review surface (AXHE-3947): per-level approver status beside
 * the offer letter itself, with approve, reject and download.
 *
 * A right-hand drawer rather than a modal. The decision itself is two controls
 * and one field, so the form is not what rules a modal out — the document is. The
 * panel is a 960px reader whose footer has to stay on screen through a multi-page
 * PDF, which the drawer paper gives as a fixed-height column with one scroll
 * region; a modal's own max-height would nest a second scroller inside it. The
 * applicant the offer concerns also stays visible behind the panel.
 *
 * ## What the panel does NOT have
 *
 * Upload New Offer is in the design and is deliberately absent. Replacing the
 * document while it is pending approval has two undecided product questions
 * behind it — who may upload, and whether an approver can see which version they
 * approved (AXHE-4016) — and on main the replacement path demotes the offer to
 * draft and cancels the request outright. Shipping the control would commit to an
 * answer by implementation accident, on a compliance surface.
 */
export const OfferReviewPanel: React.FC<OfferReviewPanelProps> = ({
  requestId,
  entry,
  onClose,
  onDecided,
}) => {
  const styles = useStyles();
  const intl = useIntl();
  const dispatch = useDispatch();
  // The decision the server accepted, so the footer confirms which one landed.
  // Held here rather than derived from the refreshed request: on an `any` level
  // with several approvers the request can still read `pending` afterwards, so
  // the only record of what *this* person just did is the response to their own
  // write.
  const [recorded, setRecorded] = useState<Decision | undefined>();
  const [decided, setDecided] = useState<ApprovalRequest | null>(null);
  const [decisionError, setDecisionError] = useState<string | undefined>();
  const [mode, setMode] = useState<PanelMode>('review');
  // The request the panel is currently reading. It starts as the one the queue
  // row opened and MOVES when an edit is resubmitted, because a resubmission
  // raises a fresh request — see `onSubmitted` below.
  const [activeRequestId, setActiveRequestId] = useState(requestId);

  const {
    data: fetched,
    isLoading,
    isError,
    refetch,
  } = useApprovalRequest(activeRequestId);
  const { mutation, result: decisionResult } = useRecordDecision(activeRequestId, {
    onSuccess: request => {
      setDecided(request);
      setDecisionError(undefined);
      onDecided();
    },
    // The server's own sentence, verbatim where there is one: it distinguishes
    // "already decided by somebody else" from "not your level" from "reason
    // required", and a generic fallback would collapse three different next
    // actions into one.
    //
    // The fallback is not optional though. `useApiServiceMutation` only unwraps a
    // body from an `ApiError`, so a network failure, a timeout or a 500 with no
    // JSON arrives here as undefined — and leaving it undefined returns the
    // button to its resting state having said nothing, on a control whose whole
    // job is recording a decision. A cancel does not reach this: the hook
    // returns on `CancelError` before calling onError.
    //
    // `recorded` is cleared here because it is set on submit. On a FIRST failure
    // the `decided ? recorded : undefined` guard below already withholds it, so
    // nothing shows. What that guard does not cover is a failure after an earlier
    // success: `decided` is still truthy from that response, so the footer would
    // render this error and the green "your decision was recorded" tick together
    // — one as `alert`, one as `status`. That is the AXHE-4183 shape, where one
    // viewer is asked for two concurrent decisions.
    onError: body => {
      setRecorded(undefined);
      setDecisionError(
        body?.message ?? intl.formatMessage(messages.decisionFailed),
      );
    },
  });

  // The post-decision tree wins over the fetched one: it is newer, and it is
  // what says the level cleared and the next one opened.
  const request = decided ?? fetched;
  const submitting = decisionResult.status === 'loading';

  // Once per open, not per render: the question is whether approvers reach this
  // surface at all, and a render-scoped call would answer a different one. Keyed
  // on the request actually being read, so a resubmission that swaps the panel
  // onto a fresh request counts as reaching that one too.
  useEffect(() => {
    trackAnalyticsEvent('offer_approvals:review_panel_view', {
      request_id: activeRequestId,
      entry,
    });
  }, [activeRequestId, entry]);

  // ---- editing the offer (AXHE-4759) ----

  const whoami = useSelector(makeSelectWhoami());
  const canEditOffers =
    whoami?.role === ADMIN_ROLE ||
    (whoami?.perms ?? []).includes(EDIT_OFFERS_PERMISSION);

  const subject = request?.subject;
  // Fail closed on every part of it. A terminal offer is one the server refuses
  // to rewrite; a missing applicant, offer or template is a payload the panel
  // cannot build a form from. The template is required rather than left to the
  // stage's default: an offer that names none has nothing to re-render from
  // either, and a stage holding several documents would otherwise resolve one
  // that is not the offer under review.
  const offerIsEditable =
    subject != null &&
    subject.offerStatus != null &&
    !UNEDITABLE_OFFER_STATUSES.includes(subject.offerStatus) &&
    Boolean(subject.applicantId) &&
    Boolean(subject.offerLetterTemplateId) &&
    Boolean(request?.subjectId) &&
    // The server's answer to whether the endpoints behind this control would
    // accept this caller. Holding the permission is not enough: an approver
    // frequently has no visibility on the applicant, and the form and the write
    // both gate on that.
    request?.viewerCanManageOffer === true;
  const canEdit = canEditOffers && offerIsEditable;

  const onSubmitted = useCallback(
    (nextRequestId: string | null) => {
      // The queue's row for the old request is gone either way — it has been
      // superseded or released — so the list refetches before the panel does.
      onDecided();
      if (nextRequestId === null) {
        // No rule matched the edited offer, so it went to the applicant instead
        // of back to approvers. There is no request left to show — and closing
        // on its own would read as the submit having failed, so the outcome
        // moves to a toast, the same hand-off Send Offer makes on a delivery.
        dispatch(
          addMessageAction(
            intl.formatMessage(messages.resubmittedNoApproval),
            'success',
          ),
        );
        onClose();
        return;
      }
      // Everything held about the OLD request is stale the moment the panel
      // points at a new one: a decision recorded against a superseded request
      // must not be confirmed under a tree that has just restarted.
      setDecided(null);
      setRecorded(undefined);
      setDecisionError(undefined);
      setMode('review');
      setActiveRequestId(nextRequestId);
    },
    [dispatch, intl, onClose, onDecided],
  );

  const edit = useOfferEdit({
    applicantId: subject?.applicantId ?? null,
    templateId: subject?.offerLetterTemplateId ?? null,
    offerId: request?.subjectId ?? null,
    storedValues: subject?.fieldValues ?? EMPTY_VALUES,
    storedConditionValues: subject?.conditionValues ?? EMPTY_VALUES,
    // The form is fetched only once somebody opens it — see `useOfferLetterForm`.
    enabled: mode !== 'review',
    onPreviewed: () => setMode('preview'),
    onSubmitted,
  });

  const submitEdit = () => {
    // Fired on submit rather than on the response, for the same reason the
    // decision event is: a resubmission the server refuses still counts as an
    // attempt, and the gap between the two is what says an approver is blocked.
    trackAnalyticsEvent('offer_approvals:offer_edit_submit', {
      request_id: activeRequestId,
      from_status: request?.status,
    });
    edit.submit();
  };

  const openEditForm = () => {
    setDecisionError(undefined);
    // Whether approvers reach for this at all, and from which state — the two
    // questions the engine cannot answer, since an edit reaches it as an
    // ordinary recruiter submit. Paired with `offer_edit_submit` below, so an
    // edit that is opened and abandoned is visible as the gap between them.
    trackAnalyticsEvent('offer_approvals:offer_edit_open', {
      request_id: activeRequestId,
      from_status: request?.status,
    });
    setMode('edit');
  };

  /**
   * Leaving the edit flow.
   *
   * From the form nothing has been written, so this is a pure discard — the
   * acceptance criterion "closing mid-edit discards the draft".
   *
   * From the preview the save has already landed, so the panel re-reads the
   * request rather than assuming it still says what it said before. On an offer
   * that was under approval that request is now cancelled, and the panel will
   * say so — which is the truth the banner warned about, not a state to hide.
   */
  const leaveEditFlow = (written: boolean) => {
    edit.reset();
    setMode('review');
    if (written) {
      refetch();
      onDecided();
    }
  };

  const handleDecide = (
    stepId: number,
    decision: Decision,
    comment?: string,
  ) => {
    setDecisionError(undefined);
    setRecorded(decision);
    // Fired on submit rather than on the response, so a decision the server
    // refuses still counts as an attempt — the gap between the two is what says
    // an approver is being blocked. `had_reason` never carries the reason
    // itself: it is free text an approver wrote about a named person's
    // compensation.
    trackAnalyticsEvent('offer_approvals:decision_submit', {
      request_id: activeRequestId,
      decision,
      had_reason: Boolean(comment),
    });
    mutation(stepId, decision, comment);
  };

  // The preview is a mode, not a status, so it wins over whatever the request
  // behind it currently reads (Design Requirements §6).
  let title = request ? TITLES[request.status] : messages.panelTitlePending;
  if (mode === 'preview') title = messages.panelTitlePreview;
  const applicantName = request?.subject.applicantName ?? null;
  const positionName = request?.subject.positionName;
  // The tree as it will be asked again. Memoized because it rebuilds every
  // level and approver, and the rail re-renders on any panel state change.
  const previewRules = useMemo(
    () => (request ? asPending(request.rules) : []),
    [request],
  );
  const showingPreview = mode === 'preview';
  // Nothing may be dropped on the floor mid-write: a decision, a save that will
  // rewrite the letter, or a submit that will restart the approval.
  const busy = submitting || edit.locked;

  return (
    // `handleClose` is MUI's `Modal#onClose`, so it fires on Escape and on a
    // backdrop click as well as from the X — none of which the X's own `disabled`
    // reaches. Closing mid-write drops the response on the floor: the decision
    // may well land, and the approver is left looking at a queue row with no idea
    // whether it did.
    <DrawerPopup
      open
      handleClose={busy ? () => {} : onClose}
      paperClassName={styles.drawer}
    >
      <DrawerHeader className={styles.panelHeader}>
        <Box
          py={2}
          px={3}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box minWidth={0}>
            {/* `h3` — 18px medium, the size every other Hire drawer titles
                itself at. Not `h5`, which is 12px in this theme: smaller than
                body. */}
            <Typography variant="h3" noWrap>
              <FormattedMessage {...title} />
            </Typography>
            {/* Absent until the request loads rather than rendering an em dash
                between two blanks. */}
            {applicantName && positionName && (
              <Typography
                variant="caption"
                color="textSecondary"
                component="div"
                noWrap
              >
                <FormattedMessage
                  {...messages.panelSubtitle}
                  values={{ applicant: applicantName, position: positionName }}
                />
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={onClose}
            // Inert while a write is in flight: none of them can be cancelled,
            // and closing mid-write would leave the queue showing a row that has
            // already been decided, or an offer whose letter is being rewritten.
            disabled={busy}
            aria-label={intl.formatMessage(globalMessages.close)}
          >
            <img src={CloseIcon} alt="" role="presentation" />
          </IconButton>
        </Box>
      </DrawerHeader>

      <DrawerContent>
        {isLoading && !request && (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress
              size={24}
              aria-label={intl.formatMessage(messages.loadingRequest)}
            />
          </Box>
        )}

        {isError && !request && (
          <Box px={3} py={4} role="alert">
            <Typography color="error">
              <FormattedMessage {...messages.requestLoadError} />
            </Typography>
          </Box>
        )}

        {request && mode === 'edit' && (
          <OfferEditForm
            applicantName={applicantName}
            edit={edit}
            // Not on a rejected offer: nothing has been approved that an edit
            // could discard, and being asked again is the point of revising
            // (Design Requirements §6).
            warnRestart={request.status !== 'rejected'}
          />
        )}

        {request && mode !== 'edit' && (
          <div className={styles.panelBody}>
            <div className={styles.rail}>
              <ApproverRail
                rules={showingPreview ? previewRules : request.rules}
                // A draft on its way back through the rules is not settled,
                // whatever the request it superseded currently reads.
                requestSettled={showingPreview ? false : isSettled(request)}
              />

              <div className={styles.railSection}>
                <Typography variant="h4">
                  <FormattedMessage {...messages.documentActionsHeading} />
                </Typography>
                {/* Stretched to the rail rather than sized to its label, so a
                    label that runs long in translation wraps inside the rail
                    instead of pushing it wider. */}
                <Button
                  fullWidth
                  size="small"
                  type="secondary"
                  // Decorative: the button carries its own visible label, so an
                  // alt here would announce the verb twice. The same glyph the
                  // recruiter's own offer preview downloads with — one icon per
                  // action across the product.
                  startIcon={
                    <img src={DownloadSvg} alt="" width={16} height={16} />
                  }
                  disabled={!request.subject.documentUrl}
                  onClick={() =>
                    window.open(
                      request.subject.documentUrl ?? '',
                      '_blank',
                      'noopener',
                    )
                  }
                >
                  <FormattedMessage {...messages.downloadOffer} />
                </Button>
              </div>

              <PreviousSubmissions submissions={request.previousSubmissions} />
            </div>

            {/* The letter, on the grey backdrop the design puts it on. Rendered
                from the real generated document rather than a template
                re-render, which is what the ticket asks for: the approver has to
                be looking at the exact PDF the candidate would receive.

                In the edited-draft preview this is the SAME url string the
                panel already held — the save is not followed by a re-read — and
                it shows the new document because the frame is unmounted while
                the form is up and because GeneratePdf overwrites one
                deterministic object key per offer. Both are load-bearing: keep
                the letter mounted through `mode === 'edit'`, or give a re-render
                a content-derived filename, and this frame starts showing
                pre-edit content with nothing to say it is stale. */}
            <div className={styles.letter}>
              {request.subject.documentUrl ? (
                <iframe
                  className={styles.letterFrame}
                  src={request.subject.documentUrl}
                  title={intl.formatMessage(messages.documentPreviewTitle)}
                />
              ) : (
                // Not a spinner: the document is missing, not loading. An offer
                // whose render failed never resolves, and a spinner would leave
                // the approver waiting on it.
                <div className={styles.letterMissing} role="alert">
                  <ErrorOutlineIcon
                    aria-hidden="true"
                    className={styles.warningIcon}
                  />
                  <Typography variant="body2">
                    <FormattedMessage {...messages.documentUnavailable} />
                  </Typography>
                </div>
              )}
            </div>
          </div>
        )}
      </DrawerContent>

      {/* A sibling of DrawerContent, not a child: the drawer paper is a
          fixed-height flex column and the content is the only part that scrolls,
          which is what holds the decision on screen for a long letter. */}
      {request && mode === 'edit' && (
        <DrawerFooter grayBackground>
          <OfferEditFooter edit={edit} onCancel={() => leaveEditFlow(false)} />
        </DrawerFooter>
      )}

      {request && showingPreview && (
        <DrawerFooter grayBackground>
          <EditedDraftFooter
            edit={edit}
            onCancel={() => leaveEditFlow(true)}
            onEdit={() => setMode('edit')}
            onSubmit={submitEdit}
          />
        </DrawerFooter>
      )}

      {request && mode === 'review' && (
        <DrawerFooter grayBackground>
          <DecisionFooter
            // The server's answer to "may this person act, and on which level",
            // not a status the client re-derives. Several levels can be
            // actionable at once when a person is named on two concurrent rules;
            // the first is the one the footer offers, and deciding it re-renders
            // this from the response with the next one.
            stepId={request.actionableStepIds[0]}
            // Why there is nothing to act on, when there is nothing. Read off
            // `isViewer` rather than tracked locally, so it is also right on a
            // request this person decided in an earlier session — and only their
            // OWN rows count, or a colleague's rejection would read as theirs.
            viewerState={viewerStateFor(request)}
            submitting={submitting}
            error={decisionError}
            recorded={decided ? recorded : undefined}
            onDecide={handleDecide}
            // The matrix's row selector, and what makes the edit control read
            // "Revise & resubmit" on a rejected offer.
            requestStatus={request.status}
            onEdit={canEdit ? openEditForm : undefined}
          />
        </DrawerFooter>
      )}
    </DrawerPopup>
  );
};
